import { Control, type ControlOptions } from "../core/Control.js";
import { makeMouseArgs, type KeyEventArgs } from "../core/events.js";
import { rectDeflate } from "../../common/geometry.js";
import type { Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { drawAlignedText, type TextAlign } from "./Label.js";
import type { ColorRef } from "../art/ArtControl.js";
import { getLook, lookContext, LookAnimator, type Look } from "../art/looks.js";

export interface ButtonOptions extends ControlOptions {
  text?: string;
  font?: FontSpec;
  /** The dialog's default button: activated by Enter, drawn as accented. */
  isDefault?: boolean;
  textAlign?: TextAlign;
  onClick?: () => void;
  /** A registered look to wear instead of the theme's button: `plate`, `medallion`, `ember`. */
  look?: string | null;
  /** The look's color; the theme's accent when absent. */
  lookColor?: ColorRef | null;
  /** Drawn lit by a look: the chosen one of a set of plates or medallions. */
  lit?: boolean;
}

/**
 * A push button.
 *
 * Space and Enter activate it while focused, matching Windows: Space fires on
 * key-up after pressing in, Enter fires immediately.
 */
export class Button extends Control {
  #text: string;
  #font: FontSpec | null;
  #keyPressed = false;

  isDefault: boolean;
  textAlign: TextAlign;
  #look: string | null;
  #lookColor: ColorRef | null;
  #lit: boolean;
  #lookAnimator = new LookAnimator(this);

  constructor(options: ButtonOptions = {}) {
    super({ width: 88, height: 30, tabStop: true, ...options });
    this.#text = options.text ?? "";
    this.#font = options.font ?? null;
    this.isDefault = options.isDefault ?? false;
    this.textAlign = options.textAlign ?? "center";
    this.#look = options.look ?? null;
    this.#lookColor = options.lookColor ?? null;
    this.#lit = options.lit ?? false;
    this.cursor = options.cursor ?? "default";
    if (options.onClick) this.on("click", options.onClick);

    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("keyup", (e) => this.#onKeyUp(e));
    this.on("blur", () => {
      if (this.#keyPressed) {
        this.#keyPressed = false;
        this.setPressed(false);
      }
    });
  }

  /**
   * A registered look to wear instead of the theme's button, or null for the theme's.
   *
   * A name nobody registered falls back to the theme rather than painting nothing: a typo in a
   * document should cost a look, not a button.
   */
  get look(): string | null {
    return this.#look;
  }
  set look(v: string | null) {
    this.#look = v;
    this.invalidate();
  }

  get lookColor(): ColorRef | null {
    return this.#lookColor;
  }
  set lookColor(v: ColorRef | null) {
    this.#lookColor = v;
    this.invalidate();
  }

  /**
   * Drawn lit by a look - the chosen one of a set of plates or medallions. Only a look draws it;
   * the theme's button does not, and it changes nothing about how the button behaves.
   */
  get lit(): boolean {
    return this.#lit;
  }
  set lit(v: boolean) {
    if (this.#lit === v) return;
    this.#lit = v;
    this.invalidate();
  }

  override get paintOverflow(): number {
    // A look glows past the button; the ember look furthest.
    return Math.max(super.paintOverflow, getLook(this.#look)?.button ? 40 : 0);
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.emit("textchanged");
    this.invalidateMeasure();
    this.invalidate();
  }

  get font(): FontSpec | null {
    return this.#font;
  }
  set font(v: FontSpec | null) {
    this.#font = v;
    this.invalidateMeasure();
    this.invalidate();
  }

  /** Fire the button as if it had been clicked. */
  performClick(): void {
    if (!this.effectivelyEnabled) return;
    this.emit("click", makeMouseArgs(this.width / 2, this.height / 2, this.toScreen({ x: 0, y: 0 }), {}));
  }

  /** Width that fits the caption plus the theme's button padding. */
  preferredWidth(): number {
    const desktop = this.desktop;
    if (!desktop) return this.width;
    const theme = desktop.theme;
    const font = this.#font ?? theme.metrics.font;
    const m = desktop.rc.measureText(this.#text, font);
    const pad = theme.metrics.buttonPadding;
    return Math.max(theme.metrics.buttonMinWidth, Math.ceil(m.width + pad.left + pad.right));
  }

  /** The caption plus its padding, at the theme's height for a control. */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const metrics = desktop.theme.metrics;
    const font = this.#font ?? metrics.font;
    const pad = metrics.buttonPadding;
    const line = desktop.rc.lineHeight(font);
    return {
      width: this.preferredWidth(),
      height: Math.max(metrics.controlHeight, Math.ceil(line + pad.top + pad.bottom)),
    };
  }

  protected override onEnvironmentChanged(): void {
    this.invalidateMeasure();
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (e.key === " ") {
      if (!this.#keyPressed) {
        this.#keyPressed = true;
        this.setPressed(true);
      }
      e.handled = true;
    } else if (e.key === "Enter") {
      this.performClick();
      e.handled = true;
    }
  }

  #onKeyUp(e: KeyEventArgs): void {
    if (e.key !== " " || !this.#keyPressed) return;
    this.#keyPressed = false;
    this.setPressed(false);
    this.performClick();
    e.handled = true;
  }

  override visualState(): VisualState {
    return { ...super.visualState(), isDefault: this.isDefault };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const look = getLook(this.#look);
    if (look?.button) {
      this.#paintLooked(rc, theme, look);
      return;
    }
    this.#lookAnimator.sync(false);
    const state = this.visualState();
    const r = this.localRect;
    const rings = this.shapeOutline();
    if (rings) theme.drawShapedChrome(rc, rings, state, "button");
    else theme.drawButton(rc, r, state);
    if (!this.#text) return;

    const font = this.#font ?? theme.metrics.font;
    let content = rectDeflate(r, theme.metrics.buttonPadding);
    // Classic buttons nudge their caption down-right while held.
    if (state.pressed) content = { ...content, x: content.x + 1, y: content.y + 1 };
    drawAlignedText(
      rc,
      this.#text,
      content,
      font,
      theme.buttonTextColor(state),
      this.textAlign,
      "middle",
      true,
    );
  }

  #paintLooked(rc: RenderContext, theme: Theme, look: Look): void {
    const ctx = lookContext(this, rc, theme, this.#lookColor, this.#lit);
    look.button!(ctx);
    this.#lookAnimator.sync(look.animates?.(ctx, "button") ?? false);
    if (!this.#text) return;
    const base = this.#font ?? theme.metrics.font;
    const font = look.buttonFont ? look.buttonFont(ctx, base) : base;
    const color = look.buttonText ? look.buttonText(ctx) : theme.buttonTextColor(ctx.state);
    // A centered look is a badge rather than a button with a margin: a medallion 44 wide loses a W
    // to the ellipsis once the theme's padding comes out of it.
    const content = look.centerText ? ctx.rect : rectDeflate(ctx.rect, theme.metrics.buttonPadding);
    const align = look.centerText ? "center" : this.textAlign;
    drawAlignedText(rc, this.#text, content, font, color, align, "middle", true);
  }
}
